import classNames from 'classnames';
import startCase from 'lodash/startCase';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import Minus from 'components/icons/actions/Minus';
import Plus from 'components/icons/actions/Plus';
import Trash from 'components/icons/Trash';
import { TEMPO } from 'context/BrandContext';
import { displayServingsText } from 'helpers/servings';
import useBrand from 'hooks/useBrand';

const MAX_SERVINGS = 8;

const AddToOrderButton = ({
  meal,
  servings,
  removeMeal,
  removeServing,
  addServing,
  disabled = false,
}) => {
  const isTempo = useBrand(TEMPO);

  const { id, photoUrl, title, displayUnit } = meal;

  return (
    <div className="crossfadeContainer">
      {servings == 0 ? (
        <Button
          className="w-full rounded-t-none border-x-0 border-t border-charcoal-60 hover:bg-tempo-navy-100 hover:text-white-100"
          variation="TextPrimary"
          onClick={() => addServing({ id, photoUrl, title })}
          disabled={disabled}
        >
          <Plus className="iconMd mr-2" />
          Add <span className="sr-only">{title}</span> to Order
        </Button>
      ) : (
        <>
          <div
            className={classNames(
              'flex w-full items-stretch overflow-hidden rounded text-white-100',
              isTempo ? 'bg-tempo-navy-100' : 'bg-green-100'
            )}
          >
            <div aria-hidden={false}>
              <Button
                className={classNames(
                  'focus:outline-3 h-full w-full cursor-pointer p-3 text-white-100 transition-colors duration-200 hover:text-white-100 focus:outline focus:outline-offset-[-1px] focus:outline-charcoal-60',
                  isTempo
                    ? 'relative bg-tempo-navy-100 hover:bg-tempo-navy-90'
                    : 'bg-green-110 hover:bg-green-110',
                  isTempo &&
                    'after:absolute after:bottom-[8px] after:left-[55px] after:h-[40px] after:w-px after:bg-[#ffffff40] after:content-[""]'
                )}
                aria-label={`Remove ${title} from order`}
                onClick={() => removeMeal({ id, servings })}
                tabIndex="0"
              >
                <Trash className={classNames('icon iconMd')} />
                <Text htmlElement="span" className="sr">
                  {title} Remove from order
                </Text>
              </Button>
            </div>
            <div className="mr-8 flex basis-[calc(100%-3.5em)] items-center justify-center">
              <Button
                aria-label={`Decrease servings for ${title}`}
                onClick={() => removeServing(id)}
              >
                <Minus className={classNames('icon iconMd')} />
              </Button>
              <Text htmlElement="span" className="text-white-100" brandStylesOverrides="textMd">
                {startCase(displayServingsText({ servings, displayUnit }))}
              </Text>
              <Button
                disabled={servings >= MAX_SERVINGS || disabled}
                aria-label={`Increase servings for ${title}`}
                onClick={() => addServing({ id, photoUrl, title })}
              >
                <Plus className={classNames('icon iconMd')} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

AddToOrderButton.propTypes = {
  meal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photoUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    displayUnit: PropTypes.object.isRequired,
  }).isRequired,
  servings: PropTypes.number.isRequired,
  removeMeal: PropTypes.func.isRequired,
  removeServing: PropTypes.func.isRequired,
  addServing: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AddToOrderButton;
